import { CloseCircleOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Descriptions,
  Flex,
  Form,
  Input,
  Radio,
  Select,
  Skeleton,
  Space,
  Typography,
  message,
} from 'antd';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { ActionDrawer } from '../../components/ActionDrawer';
import { BillSaleDrawer } from '../../components/BillSaleDrawer';
import { DetailPage } from '../../components/DetailPage';
import { BookingMoneyCard } from './BookingMoneyCard';
import { invalidateAfterBookingMoney } from './invalidate-booking-money';
import { FinancingPanel } from './FinancingPanel';
import { ApiError } from '../../lib/api-client';
import { formatDate, formatDateTime, orDash } from '../../lib/format';
import { useDebounced } from '../../lib/use-debounced';
import { bookingLabelKey, bookingTone, depositOutcomeKey, meetsMinimumDeposit } from './booking-status';
import {
  cancelBooking,
  clearPending,
  getBooking,
  getBookingMoney,
  listAvailableUnits,
  sendToCashier,
  type AvailableUnit,
  type BookingCancelledBy,
} from './bookings-api';
import { formatMoney } from '../../lib/money';

const { Text } = Typography;

interface VariantMismatch {
  message: string;
  quotedItemCode: string | null;
  unitItemCode: string | null;
}

/** The API's "this chassis is not the variant quoted" refusal, or null for any other error. */
function variantMismatchFrom(e: unknown): VariantMismatch | null {
  if (!(e instanceof ApiError) || e.code !== 'BOOKING_INVALID' || !('quotedItemCode' in e.details)) {
    return null;
  }
  const code = (v: unknown): string | null => (typeof v === 'string' ? v : null);
  return {
    message: e.message,
    quotedItemCode: code(e.details['quotedItemCode']),
    unitItemCode: code(e.details['unitItemCode']),
  };
}

/**
 * The API's "this bike is reserved for another booking" refusal that a manager
 * can override, or null. A bike already BILLED to someone also comes back as a
 * 409, without `required`: no reason moves that one, so it stays a plain error.
 */
function allocationConflictFrom(e: unknown): string | null {
  if (!(e instanceof ApiError) || e.status !== 409 || e.details['required'] !== 'sales.override_allocation') {
    return null;
  }
  return e.message;
}

export function BookingDetailPage(): ReactNode {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { uid = '' } = useParams<{ uid: string }>();

  const [sending, setSending] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [billing, setBilling] = useState(false);
  const [unitUid, setUnitUid] = useState<string | undefined>(undefined);
  const [depositAmount, setDepositAmount] = useState('');
  const [cancelledBy, setCancelledBy] = useState<BookingCancelledBy>('CUSTOMER');
  const [cancelReason, setCancelReason] = useState('');
  // Set when the API refuses the chosen chassis as a different variant from the
  // one quoted. The drawer then asks for a manager's reason; the API decides
  // whether this person may give one (sales.override_allocation).
  const [variantMismatch, setVariantMismatch] = useState<VariantMismatch | null>(null);
  const [variantReason, setVariantReason] = useState('');
  // Set when the chosen bike is reserved for another booking. A manager can
  // take it with a reason; the cashier applies it when the deposit is taken.
  const [allocationConflict, setAllocationConflict] = useState<string | null>(null);
  const [allocationReason, setAllocationReason] = useState('');
  // Any other refusal (a bike billed to someone else, a closed booking) stays
  // in the drawer: the advisor's next step is another chassis, not a retry.
  const [sendRefusal, setSendRefusal] = useState<string | null>(null);
  // The quoted variant's chassis are offered first; other variants only when asked.
  const [showOther, setShowOther] = useState(false);
  const [unitTerm, setUnitTerm] = useState('');
  const debouncedUnitTerm = useDebounced(unitTerm.trim(), 300);

  const { data, isLoading } = useQuery({ queryKey: ['booking', uid], queryFn: () => getBooking(uid) });
  // Shared with BookingMoneyCard (same key): whether the sale is billed yet.
  const money = useQuery({ queryKey: ['booking-money', uid], queryFn: () => getBookingMoney(uid) });

  // Only fetched while the drawer is open: an advisor reading a booking has no
  // use for the stock list, and it changes underneath them the moment they do.
  // Filtered on the server: with hundreds of bikes in stock, the quoted
  // variant's chassis is rarely in an unfiltered first page.
  const quotedItemCode = data?.quotedItemCode ?? null;
  const searchingAll = showOther || quotedItemCode === null;
  const quotedUnits = useQuery({
    queryKey: ['units', 'available', 'variant', quotedItemCode],
    queryFn: () => listAvailableUnits({ itemCode: quotedItemCode ?? '' }),
    enabled: sending && quotedItemCode !== null,
  });
  const otherUnits = useQuery({
    queryKey: ['units', 'available', 'search', debouncedUnitTerm],
    queryFn: () => listAvailableUnits(debouncedUnitTerm.length > 0 ? { q: debouncedUnitTerm } : {}),
    enabled: sending && searchingAll,
  });
  const shownUnits: AvailableUnit[] = (searchingAll ? otherUnits.data : quotedUnits.data)?.items ?? [];
  const unitsLoading = searchingAll ? otherUnits.isFetching : quotedUnits.isFetching;
  const [chosenUnit, setChosenUnit] = useState<AvailableUnit | undefined>(undefined);
  const isOtherVariant = (u: AvailableUnit): boolean =>
    quotedItemCode !== null &&
    (u.autocountItemCode === null || u.autocountItemCode.toUpperCase() !== quotedItemCode.toUpperCase());

  const refresh = (): void => {
    invalidateAfterBookingMoney(queryClient, uid);
  };

  const fail = (e: unknown, fallback: string): void => {
    void message.error(e instanceof ApiError ? e.message : fallback);
  };

  const send = useMutation({
    mutationFn: () =>
      sendToCashier(uid, {
        unitUid: unitUid ?? '',
        depositAmount: depositAmount.trim(),
        ...(allocationConflict !== null ? { overrideReason: allocationReason.trim() } : {}),
        ...(variantMismatch !== null ? { variantOverrideReason: variantReason.trim() } : {}),
      }),
    onSuccess: (result) => {
      refresh();
      setSending(false);
      setVariantMismatch(null);
      setAllocationConflict(null);
      void message.success(t('bookings.sent', { chassis: result.pendingChassisNo ?? '' }));
    },
    onError: (e: unknown) => {
      // Keep the drawer open on either refusal: the way forward is a manager's
      // reason, not a phone call.
      const mismatch = variantMismatchFrom(e);
      if (mismatch !== null) {
        setVariantMismatch(mismatch);
        return;
      }
      const conflict = allocationConflictFrom(e);
      if (conflict !== null) {
        setAllocationConflict(conflict);
        return;
      }
      setSendRefusal(e instanceof ApiError ? e.message : t('bookings.sendFailed'));
    },
  });

  const takeOffTill = useMutation({
    mutationFn: () => clearPending(uid),
    onSuccess: () => {
      refresh();
      void message.success(t('bookings.clearedPending'));
    },
    onError: (e: unknown) => {
      fail(e, t('bookings.clearPendingFailed'));
    },
  });

  const cancel = useMutation({
    mutationFn: () => cancelBooking(uid, { cancelledBy, reason: cancelReason.trim() }),
    onSuccess: (result) => {
      refresh();
      setCancelling(false);
      void message.success(t(`bookings.cancelled.${result.depositOutcome}`));
    },
    onError: (e: unknown) => {
      fail(e, t('bookings.cancelFailed'));
    },
  });

  if (isLoading || data === undefined) {
    return (
      <Card>
        <Skeleton active paragraph={{ rows: 6 }} />
      </Card>
    );
  }

  // OPEN, or a confirmed booking whose bike an override gave away (Q109): either
  // way, the deposit taken at the Till on the bike chosen here reserves it.
  const canSend = data.status === 'OPEN' || data.bikeTaken;
  // Billing needs a reserved bike and no invoice yet. The money query is the
  // only place the invoice shows, so the button waits for it.
  const canBill =
    data.status === 'CONFIRMED' && !data.bikeTaken && money.data !== undefined && money.data.invoice === null;
  const canCancel = data.status === 'OPEN' || data.status === 'CONFIRMED';
  const depositOk = meetsMinimumDeposit(depositAmount);
  const currency = data.currency;

  return (
    <>
      <DetailPage
        title={data.customerName}
        subtitle={data.bookingNo}
        tags={[
          // "No bike yet" until sent to the cashier; "Awaiting deposit" while it is on the Till.
          { label: t(bookingLabelKey(data)), colour: bookingTone(data.status, data.bikeTaken) },
          ...(data.hasLapsed ? [{ label: t('bookings.lapsed'), colour: 'error' }] : []),
        ]}
        onBack={() => {
          void navigate('/bookings');
        }}
        actions={
          <Space wrap>
            {canSend && (
              <Button
                type="primary"
                onClick={() => {
                  setUnitUid(undefined);
                  // Changing what was sent starts from the deposit already asked for.
                  setDepositAmount(data.pendingDepositAmount ?? '');
                  setVariantMismatch(null);
                  setVariantReason('');
                  setAllocationConflict(null);
                  setAllocationReason('');
                  setSendRefusal(null);
                  setShowOther(false);
                  setUnitTerm('');
                  setChosenUnit(undefined);
                  setSending(true);
                }}
              >
                {t(data.awaitingDeposit ? 'bookings.changeSend' : 'bookings.send')}
              </Button>
            )}
            {canBill && !billing && (
              <Button
                type="primary"
                onClick={() => {
                  setBilling(true);
                }}
              >
                {t('bookings.bill')}
              </Button>
            )}
            {canCancel && (
              <Button
                danger
                icon={<CloseCircleOutlined />}
                onClick={() => {
                  setCancelledBy('CUSTOMER');
                  setCancelReason('');
                  setCancelling(true);
                }}
              >
                {t('bookings.cancel')}
              </Button>
            )}
          </Space>
        }
        tabs={[
          {
            key: 'overview',
            label: t('sales.tabs.overview'),
            content: (
              <Flex vertical gap={16}>
                {data.status === 'CONFIRMED' && money.error !== null && (
                  // Without the money we cannot tell whether the sale is billed,
                  // so Bill this sale is withheld — and the advisor is told why.
                  <Alert
                    type="error"
                    showIcon
                    message={t('bookings.billUnknown')}
                    description={money.error instanceof ApiError ? money.error.message : undefined}
                    action={
                      <Button
                        size="large"
                        loading={money.isFetching}
                        onClick={() => {
                          void money.refetch();
                        }}
                      >
                        {t('bookings.retry')}
                      </Button>
                    }
                  />
                )}
                {canSend && !data.awaitingDeposit && (
                  <Alert
                    type="warning"
                    showIcon
                    message={t(data.bikeTaken ? 'bookings.bikeTakenTitle' : 'bookings.notReserved')}
                    description={t(data.bikeTaken ? 'bookings.bikeTakenHelp' : 'bookings.notReservedHelp')}
                  />
                )}
                {canSend && data.awaitingDeposit && (
                  <Alert
                    type="warning"
                    showIcon
                    message={t('bookings.awaitingHelp', {
                      amount: formatMoney(data.pendingDepositAmount ?? '0', currency),
                      chassis: data.pendingChassisNo ?? '',
                    })}
                    description={
                      data.pendingOverrideBy === null
                        ? undefined
                        : t('bookings.overrideBy', { name: data.pendingOverrideBy })
                    }
                    action={
                      <Button
                        size="large"
                        loading={takeOffTill.isPending}
                        onClick={() => {
                          takeOffTill.mutate();
                        }}
                      >
                        {t('bookings.clearPending')}
                      </Button>
                    }
                  />
                )}
                {data.hasLapsed && data.status === 'CONFIRMED' && (
                  <Alert
                    type="error"
                    showIcon
                    message={t('bookings.lapsedTitle', { date: formatDate(data.validUntil ?? '') })}
                    description={t('bookings.lapsedHelp')}
                  />
                )}
                {data.status === 'CANCELLED' && (
                  <Alert
                    type="info"
                    showIcon
                    message={t('bookings.cancelledTitle', {
                      who:
                        data.cancelledBy === 'CUSTOMER' ? t('bookings.byCustomer') : t('bookings.byDealer'),
                    })}
                    description={
                      <Flex vertical gap={4}>
                        <Text>{orDash(data.cancelReason)}</Text>
                        <Text strong>
                          {t('bookings.depositWas', {
                            outcome: t(depositOutcomeKey(data.depositOutcome)).toLowerCase(),
                            amount: formatMoney(data.depositAmount, currency),
                          })}
                        </Text>
                      </Flex>
                    }
                  />
                )}

                <BookingMoneyCard bookingUid={data.uid} />

                <Card title={t('bookings.theBike')}>
                  {data.chassisNo === null ? (
                    <Text type="secondary">{t('bookings.noBikeYet')}</Text>
                  ) : (
                    <Descriptions column={{ xs: 1, sm: 1, md: 2 }} bordered size="small">
                      <Descriptions.Item label={t('bookings.fields.chassisNo')}>
                        <Text strong>{data.chassisNo}</Text>
                      </Descriptions.Item>
                      <Descriptions.Item label={t('sales.fields.model')}>
                        {orDash(data.model)}
                      </Descriptions.Item>
                    </Descriptions>
                  )}
                </Card>

                <Card>
                  <Descriptions column={{ xs: 1, sm: 1, md: 2 }} bordered size="small">
                    <Descriptions.Item label={t('sales.fields.customer')}>
                      {data.customerName}
                    </Descriptions.Item>
                    <Descriptions.Item label={t('sales.fields.quotationNo')}>
                      {orDash(data.quotationNo)}
                    </Descriptions.Item>
                    <Descriptions.Item label={t('bookings.fields.deposit')}>
                      <Text strong>{formatMoney(data.depositAmount, currency)}</Text>
                    </Descriptions.Item>
                    <Descriptions.Item label={t('bookings.fields.depositRef')}>
                      {orDash(data.depositRef)}
                    </Descriptions.Item>
                    <Descriptions.Item label={t('bookings.fields.validUntil')}>
                      {data.validUntil === null ? (
                        '—'
                      ) : (
                        <Text type={data.hasLapsed ? 'danger' : undefined}>
                          {formatDate(data.validUntil)}
                        </Text>
                      )}
                    </Descriptions.Item>
                    <Descriptions.Item label={t('bookings.fields.confirmedAt')}>
                      {data.confirmedAt === null ? '—' : formatDateTime(data.confirmedAt)}
                    </Descriptions.Item>
                    <Descriptions.Item label={t('bookings.fields.expectedDelivery')}>
                      {data.expectedDeliveryAt === null ? '—' : formatDate(data.expectedDeliveryAt)}
                    </Descriptions.Item>
                    <Descriptions.Item label={t('sales.fields.created')}>
                      {formatDateTime(data.createdAt)}
                    </Descriptions.Item>
                  </Descriptions>
                </Card>
              </Flex>
            ),
          },
          {
            key: 'financing',
            label: t('financing.tab'),
            content: <FinancingPanel bookingUid={data.uid} canApply={data.status !== 'CANCELLED'} />,
          },
        ]}
      />

      <ActionDrawer
        open={sending}
        title={t('bookings.sendTitle')}
        submitLabel={t(variantMismatch === null ? 'bookings.sendSubmit' : 'bookings.variantOverrideSubmit')}
        submitting={send.isPending}
        submitDisabled={
          unitUid === undefined ||
          !depositOk ||
          (variantMismatch !== null && variantReason.trim().length < 3) ||
          (allocationConflict !== null && allocationReason.trim().length < 3)
        }
        onClose={() => {
          setSending(false);
        }}
        onSubmit={() => {
          setSendRefusal(null);
          send.mutate();
        }}
        // CLAUDE.md §10: say what will change before it changes — and what does
        // not: sending holds nothing until the cashier takes the deposit.
        consequence={
          unitUid === undefined || !depositOk ? undefined : (
            <Flex vertical gap={4}>
              {variantMismatch !== null && (
                <span>
                  {t('bookings.variantOverrideConsequence', {
                    chassis: chosenUnit?.chassisNo ?? '',
                    unitCode: variantMismatch.unitItemCode ?? t('bookings.noItemCode'),
                    quotedCode: variantMismatch.quotedItemCode ?? t('bookings.noItemCode'),
                    name: data.customerName,
                    amount: formatMoney(depositAmount.trim(), currency),
                  })}
                </span>
              )}
              {allocationConflict !== null && (
                <span>
                  {t('bookings.allocationOverrideConsequence', {
                    chassis: chosenUnit?.chassisNo ?? '',
                    amount: formatMoney(depositAmount.trim(), currency),
                  })}
                </span>
              )}
              {variantMismatch === null && allocationConflict === null && (
                <span>
                  {t('bookings.sendConsequence', {
                    chassis: chosenUnit?.chassisNo ?? '',
                    amount: formatMoney(depositAmount.trim(), currency),
                  })}
                </span>
              )}
            </Flex>
          )
        }
        error={
          variantMismatch === null && allocationConflict === null && sendRefusal === null ? undefined : (
            <Flex vertical gap={8}>
              {sendRefusal !== null && <Alert type="error" showIcon message={sendRefusal} />}
              {variantMismatch !== null && (
                <Alert
                  type="warning"
                  showIcon
                  message={variantMismatch.message}
                  description={t('bookings.variantMismatchHelp')}
                />
              )}
              {allocationConflict !== null && (
                <Alert
                  type="warning"
                  showIcon
                  message={allocationConflict}
                  description={t('bookings.allocationConflictHelp')}
                />
              )}
            </Flex>
          )
        }
      >
        <Form layout="vertical">
          {data.quotedItemCode !== null && (
            <Alert
              type="info"
              message={t('bookings.quotedVariant', {
                bike: data.quotedBike ?? '',
                code: data.quotedItemCode,
              })}
            />
          )}
          <Form.Item
            label={t('bookings.fields.chooseUnit')}
            required
            help={searchingAll ? undefined : t('bookings.quotedVariantOnly')}
          >
            <Select
              showSearch
              size="large"
              value={unitUid}
              loading={unitsLoading}
              onChange={(v: string) => {
                setUnitUid(v);
                setChosenUnit(shownUnits.find((u) => u.uid === v));
                // The refusal was about that chassis; another one starts fresh.
                setVariantMismatch(null);
                setVariantReason('');
                setAllocationConflict(null);
                setAllocationReason('');
                setSendRefusal(null);
              }}
              placeholder={t('bookings.chooseUnitPlaceholder')}
              // Searching every variant is the server's job; within the quoted
              // variant the list is short enough to filter here.
              filterOption={searchingAll ? false : undefined}
              optionFilterProp="label"
              onSearch={searchingAll ? setUnitTerm : undefined}
              notFoundContent={
                unitsLoading
                  ? t('common.loading')
                  : t(searchingAll ? 'bookings.noUnits' : 'bookings.noQuotedUnits')
              }
              options={shownUnits.map((u) => ({
                value: u.uid,
                label: `${u.chassisNo} · ${u.model ?? ''} ${u.colour ?? ''}`.trim(),
                otherVariant: isOtherVariant(u),
              }))}
              // The flag goes on its own line so a phone never cuts it off.
              optionRender={(option) => (
                <div>
                  <div>{option.label}</div>
                  {option.data.otherVariant && (
                    <Text type="warning">{t('bookings.otherVariantFlag')}</Text>
                  )}
                </div>
              )}
            />
          </Form.Item>
          {quotedItemCode !== null && (
            <Form.Item>
              <Checkbox
                checked={showOther}
                onChange={(e) => {
                  setShowOther(e.target.checked);
                  setUnitTerm('');
                  // The other list may not hold the chassis chosen from this one.
                  setUnitUid(undefined);
                  setChosenUnit(undefined);
                  setVariantMismatch(null);
                  setVariantReason('');
                  setAllocationConflict(null);
                  setAllocationReason('');
                  setSendRefusal(null);
                }}
              >
                {t('bookings.showOtherVariants')}
              </Checkbox>
            </Form.Item>
          )}
          <Form.Item
            label={t('bookings.fields.deposit')}
            required
            validateStatus={depositAmount.length > 0 && !depositOk ? 'error' : undefined}
            help={
              depositAmount.length > 0 && !depositOk
                ? t('bookings.depositTooSmall')
                : t('bookings.depositHelp')
            }
          >
            <Input
              inputMode="decimal"
              prefix="MYR"
              size="large"
              value={depositAmount}
              onChange={(e) => {
                setDepositAmount(e.target.value);
              }}
              placeholder="300.00"
            />
          </Form.Item>
          {variantMismatch !== null && (
            <Form.Item label={t('bookings.variantReasonLabel')} htmlFor="variant-override-reason" required>
              <Input.TextArea
                id="variant-override-reason"
                size="large"
                rows={3}
                maxLength={255}
                value={variantReason}
                onChange={(e) => {
                  setVariantReason(e.target.value);
                }}
                placeholder={t('bookings.variantReasonPlaceholder')}
              />
            </Form.Item>
          )}
          {allocationConflict !== null && (
            <Form.Item label={t('bookings.allocationReasonLabel')} htmlFor="allocation-override-reason" required>
              <Input.TextArea
                id="allocation-override-reason"
                size="large"
                rows={3}
                maxLength={255}
                value={allocationReason}
                onChange={(e) => {
                  setAllocationReason(e.target.value);
                }}
                placeholder={t('bookings.allocationReasonPlaceholder')}
              />
            </Form.Item>
          )}
        </Form>
      </ActionDrawer>

      <ActionDrawer
        open={cancelling}
        danger
        title={t('bookings.cancelTitle')}
        submitLabel={t('bookings.cancelSubmit')}
        submitting={cancel.isPending}
        submitDisabled={cancelReason.trim().length < 3}
        onClose={() => {
          setCancelling(false);
        }}
        onSubmit={() => {
          cancel.mutate();
        }}
        // Who cancelled decides the deposit — it is not a separate choice
        // (Q11c). Saying so here is what stops it being argued about later.
        consequence={t(
          cancelledBy === 'CUSTOMER'
            ? 'bookings.cancelConsequenceCustomer'
            : 'bookings.cancelConsequenceDealer',
          {
            amount: formatMoney(data.depositAmount, currency),
            chassis: data.chassisNo ?? '',
          },
        )}
      >
        <Form layout="vertical">
          <Form.Item label={t('bookings.fields.cancelledBy')} required>
            <Radio.Group
              value={cancelledBy}
              onChange={(e) => {
                setCancelledBy(e.target.value as BookingCancelledBy);
              }}
              optionType="button"
              buttonStyle="solid"
              size="large"
              options={[
                { value: 'CUSTOMER', label: t('bookings.byCustomer') },
                { value: 'DEALER', label: t('bookings.byDealer') },
              ]}
            />
          </Form.Item>
          <Form.Item label={t('bookings.fields.cancelReason')} required>
            <Input.TextArea
              rows={3}
              value={cancelReason}
              onChange={(e) => {
                setCancelReason(e.target.value);
              }}
              placeholder={t('bookings.cancelReasonPlaceholder')}
            />
          </Form.Item>
        </Form>
      </ActionDrawer>

      <BillSaleDrawer
        bookingUid={data.uid}
        open={billing}
        onClose={() => {
          setBilling(false);
        }}
        onBilled={refresh}
      />
    </>
  );
}
